import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { addToCart } from '../../lib/cart';
import { ArrowLeft, ArrowRight, ArrowUpRight, Gauge, ImageOff, Package2, Ruler, Weight, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useScrollContainer } from '../../context/ScrollContainerContext';
import { formatTND } from '../../lib/currency';
import { localizePath } from '../../lib/localizedRoutes';
import { getBrandImageSrc } from '../../lib/brandImage';
import { getActivePromo, getPromoFlashColor, getPromoToneClasses, sortItemsByPromo } from '../../lib/promo';
import { getProductBrand } from '../../lib/productBrand';
import { shouldShowPrices } from '../../lib/priceVisibility';
import CroppedImage from '../CroppedImage';
import Skeleton from '../Skeleton';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';


const PENDING_CART_KEY = 'etc_pending_cart_add';

function formatSpecLabel(key = '') {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatSpecValue(value, t) {
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (typeof value === 'number') return String(value);
  return String(value || '').trim();
}

function groupProductsByType(products = [], fallbackType) {
  return products.reduce((groups, product) => {
    const typeKey = String(product?.productType || '').trim() || fallbackType;
    if (!groups.has(typeKey)) {
      groups.set(typeKey, []);
    }
    groups.get(typeKey).push(product);
    return groups;
  }, new Map());
}

function getTypeBadgeClassName(typeKey = '') {
  const normalized = String(typeKey || '').trim().toLowerCase();
  if (normalized === 'forklift') {
    return 'bg-[rgba(255,93,16,0.92)] text-white border border-[rgba(255,140,92,0.65)]';
  }
  return 'bg-black/70 text-white/90';
}

function getPreferredSpecOrder(typeKey = '') {
  const normalized = String(typeKey || '').trim().toLowerCase();
  if (normalized === 'forklift') {
    return [
      'loading_capacity',
      'power_type',
      'engine_model',
      'battery',
      'load_center',
      'mast_lifting_height',
      'turning_radius',
      'overall_width',
      'fork',
    ];
  }
  if (normalized === 'pallet_truck') {
    return [
      'capacity',
      'power_type',
      'battery',
      'load_center',
      'fork_length',
      'overall_fork_width',
      'lifting_height',
      'self_weight',
    ];
  }
  if (normalized === 'stacker') {
    return [
      'capacity',
      'power_type',
      'battery',
      'load_center',
      'max_lifting_height',
      'fork_length',
      'turning_radius',
      'service_weight',
    ];
  }
  return ['capacity', 'loading_capacity', 'power_type', 'battery', 'load_center', 'fork_length'];
}

function getCardSpecs(product, limit = 3) {
  const specs =
    product?.specs && typeof product.specs === 'object' && !Array.isArray(product.specs) ? product.specs : {};
  const entries = Object.entries(specs).filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (!entries.length) return [];

  const excluded = new Set(['brand', 'model']);
  const filtered = entries.filter(([key]) => !excluded.has(String(key || '').trim().toLowerCase()));
  const preferred = getPreferredSpecOrder(product?.productType);
  const byKey = new Map(filtered);
  const picked = [];

  preferred.forEach((key) => {
    if (!byKey.has(key) || picked.length >= limit) return;
    picked.push([key, byKey.get(key)]);
    byKey.delete(key);
  });

  for (const [key, value] of byKey.entries()) {
    if (picked.length >= limit) break;
    picked.push([key, value]);
  }

  return picked;
}

function getSpecIcon(key = '') {
  const normalized = String(key || '').trim().toLowerCase();

  if (
    normalized.includes('capacity') ||
    normalized.includes('load') ||
    normalized.includes('weight')
  ) {
    return Weight;
  }

  if (
    normalized.includes('speed') ||
    normalized.includes('battery') ||
    normalized.includes('power') ||
    normalized.includes('engine') ||
    normalized.includes('motor')
  ) {
    return Zap;
  }

  if (
    normalized.includes('height') ||
    normalized.includes('width') ||
    normalized.includes('length') ||
    normalized.includes('radius') ||
    normalized.includes('fork') ||
    normalized.includes('dimension') ||
    normalized.includes('center')
  ) {
    return Ruler;
  }

  if (normalized.includes('drive') || normalized.includes('traction')) {
    return Gauge;
  }

  return Package2;
}

function getVisiblePageNumbers(currentPage, totalPages) {
  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function buildProductCardImageAlt(product, typeLabel, language) {
  const name = String(product?.name || '').trim();
  const type = String(typeLabel || '').trim();

  if (language === 'fr') {
    return [name, type ? `photo ${type.toLowerCase()}` : 'photo produit'].filter(Boolean).join(' - ');
  }

  return [name, type ? `${type} photo` : 'product photo'].filter(Boolean).join(' - ');
}

export default function ProductsGridSection({
  products,
  isLoading,
  productTypeMetaByKey = new Map(),
  brandRegistry = [],
  totalCountByType = {},
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
}) {
  const { t, language } = useI18n();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const scrollContainerRef = useScrollContainer();
  const gridRef = useRef(null);
  const lastScrolledPageRef = useRef(currentPage);
  const groupedProducts = groupProductsByType(products, 'unclassified');
  const visiblePages = getVisiblePageNumbers(currentPage, totalPages);

  // When the user clicks a pagination control, scroll the grid back to the
  // top of the visible viewport. Without this the user can end up staring at
  // the footer (especially on mobile) if they were already scrolled near
  // the bottom of the previous page. We anchor on the grid container (not
  // the wrapping <section>) so the first card sits cleanly below the sticky
  // header regardless of any decorative padding above the grid. The Layout
  // uses a custom scroll container, so we have to scroll that element
  // rather than the window. We skip the very first render to avoid fighting
  // the natural initial scroll position (e.g. deep links / browser-restored
  // scroll).
  useEffect(() => {
    if (lastScrolledPageRef.current === currentPage) return;
    lastScrolledPageRef.current = currentPage;

    const container = scrollContainerRef?.current;
    const target = gridRef.current;
    if (!target) return;

    // Measure the sticky header so we leave breathing room between it and
    // the first card. Fall back to a sensible default when the header
    // cannot be measured (e.g. tests, SSR).
    const stickyHeader = document.querySelector('header')?.closest('.sticky');
    const headerHeight = stickyHeader?.getBoundingClientRect().height ?? 80;
    const extraOffset = 16;

    if (container) {
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const offsetWithinContainer = targetTop - containerTop;
      const nextTop = container.scrollTop + offsetWithinContainer - headerHeight - extraOffset;
      container.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
    } else if (typeof window !== 'undefined') {
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [currentPage, scrollContainerRef]);

  function shouldIgnoreCardClick(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('a,button,input,select,textarea,label,[role="button"]'));
  }

  function handleCardNavigation(event, itemId) {
    if (!itemId || shouldIgnoreCardClick(event.target)) return;
    router.push(localizePath(`/machines/${itemId}`, language));
  }

  function handleCardKeyDown(event, itemId) {
    if (!itemId || shouldIgnoreCardClick(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      router.push(localizePath(`/machines/${itemId}`, language));
    }
  }

  function handleAddToCart(product) {
    if (!product) return;
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify({ ...product, quantity: 1, kind: 'product' }));
      }
      router.push(`/auth/login?next=${encodeURIComponent(router.asPath || '/machines')}`);
      return;
    }
    addToCart(product, 1, { kind: 'product' });
    toast.success(`${product.name} ${t('common.addedToCart', 'added to cart.')}`);
  }

  return (
    <section className="">
      <div className="container mx-auto px-6 lg:px-10">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-[var(--dash-black)] p-4">
                <Skeleton className="h-44 w-full rounded-xl bg-white/10" showLogo />
                <Skeleton className="mt-4 h-4 w-2/3 bg-white/10" />
                <Skeleton className="mt-2 h-3 w-5/6 bg-white/10" />
                <Skeleton className="mt-4 h-3 w-1/3 bg-white/10" />
              </div>
            ))}
          </div>
        ) : (
          <div ref={gridRef} className="space-y-12">
            {products.length === 0 && (
              <div className="rounded-2xl border border-dashed border-white/10 bg-[var(--dash-black)] p-10 text-center">
                <p className="text-lg font-semibold text-white">{t('productsPage.grid.noMatchesTitle')}</p>
                <p className="mt-2 text-sm text-white/60">{t('productsPage.grid.noMatchesText')}</p>
              </div>
            )}

            {Array.from(groupedProducts.entries()).map(([typeKey, typedProducts]) => {
              const typeCount = Number(totalCountByType?.[typeKey] || typedProducts.length || 0);
              return (
              <section key={typeKey} className="space-y-6">
                <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] px-8 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.22)]">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--dash-main)]/70 to-transparent" />
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--dash-main)]/85">
                        <span className="h-2 w-2 rounded-full bg-[var(--dash-main)] shadow-[0_0_16px_rgba(var(--dash-main-rgb),0.45)]" />
                        {t('productsPage.hero.categories')}
                      </span>
                      <h2 className="mt-1 text-2xl font-semibold uppercase tracking-[0.04em] text-white sm:text-[1.9rem]">
                        {productTypeMetaByKey.get(typeKey)?.label || typeKey}
                      </h2>
                    </div>
                    <span className="inline-flex w-fit items-center rounded-full border border-[var(--dash-main)]/25 bg-[var(--dash-main)]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                      {typeCount} {t('productsPage.filters.products')}
                    </span>
                  </div>
                </div>

                <div className="grid gap-7 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {sortItemsByPromo(typedProducts).map((product) => {
                    const typeKey = String(product.productType || 'unclassified');
                    const typeMeta = productTypeMetaByKey.get(typeKey) || null;
                    const fieldMetaByKey = typeMeta?.fields || new Map();
                    const typeLabel = typeMeta?.label || typeKey;
                    const imageAlt = buildProductCardImageAlt(product, typeLabel, language);
                    const specsEntries = getCardSpecs(product, 3);
                    const activePromo = getActivePromo(product, language);
                    const promoTone = getPromoToneClasses(activePromo?.colorTone);
                    const showPrices = shouldShowPrices();
                    const brand = getProductBrand(product);
                    const brandImageSrc = getBrandImageSrc(brand, brandRegistry);

                    return (
                      <article
                        key={product._id}
                        role="link"
                        tabIndex={0}
                        onClick={(event) => handleCardNavigation(event, product._id)}
                        onKeyDown={(event) => handleCardKeyDown(event, product._id)}
                        className="group isolate relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[26px] border border-white/10 bg-[var(--dash-black)] shadow-[0_22px_55px_rgba(0,0,0,0.2)] transition-colors duration-300 hover:border-[var(--dash-main)]/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-main)]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dash-black)]"
                      >
                <div className="relative z-20 aspect-[4/3] overflow-hidden bg-[#111]">
                  {product.images && product.images.length > 0 ? (
                    <div className="relative h-full w-full">
                      <ImageOnlySkeletonLoader
                        image={product.images[0]}
                        mode="native"
                        alt={imageAlt}
                        containerClassName="relative h-full w-full object-cover"
                        imgClassName="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        skeletonEnabled
                        skeletonRadius="none"
                      />
                    </div>
                  ) : (

                            <div className="relative flex h-full flex-col items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,rgba(var(--dash-main-rgb),0.16),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-5 text-center">
                              <div className="relative flex h-14 w-14 items-center justify-center rounded-[20px] border border-white/10 bg-white/5 text-[var(--dash-primary)] shadow-[0_20px_50px_-30px_rgba(var(--dash-main-rgb),0.5)]">
                                <ImageOff className="h-6 w-6" />
                              </div>
                              <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.26em] text-white/45">
                                {t('common.noImage')}
                              </p>
                              <p className="mt-2 max-w-[12rem] text-xs uppercase tracking-[0.16em] text-white/35">
                                {t('productDetails.visualComingSoon')}
                              </p>
                            </div>
                          )}
                          <span
                            className={`absolute left-3 top-3 z-30 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getTypeBadgeClassName(typeKey)}`}
                          >
                            {productTypeMetaByKey.get(typeKey)?.label || typeKey}
                          </span>
                          {activePromo ? (
                            <span
                              className={`promo-badge-flash absolute right-3 top-3 z-30 overflow-visible rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${promoTone.badge}`}
                              style={{ '--promo-flash-color': getPromoFlashColor(activePromo?.colorTone) }}
                            >
                              {activePromo.label || activePromo.title || t('common.promo', 'Promo')}
                            </span>
                          ) : null}
                          {brandImageSrc ? (
                            <span className="absolute bottom-3 right-3 z-30 inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-black/70 p-2 opacity-0 translate-y-2 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 shadow-[0_14px_30px_rgba(0,0,0,0.28)] backdrop-blur-sm md:h-12 md:w-12">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={brandImageSrc} alt={`${brand} logo`} className="h-full w-full object-contain" />
                            </span>
                          ) : null}
                        </div>

                        <div className="relative z-20 flex flex-1 flex-col gap-5 p-5">
                          <div className="space-y-3">
                            <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-white">
                              {product.name}
                            </h3>
                            {activePromo?.title || activePromo?.description ? (
                              <div className={`rounded-xl border px-3 py-2 text-xs ${promoTone.callout}`}>
                                <p className="font-semibold uppercase tracking-[0.14em]">
                                  {activePromo.title || activePromo.label || t('common.promo', 'Promo')}
                                </p>
                                {activePromo.description ? (
                                  <p className="mt-1 text-white/80">{activePromo.description}</p>
                                ) : null}
                              </div>
                            ) : null}
                            <div className="h-px w-12 bg-[var(--dash-main)]/45" />
                          </div>
                          {specsEntries.length > 0 ? (
                            <div className="border-t border-white/20 pt-4">
                              <div className="grid gap-2.5">
                                {specsEntries.map(([key, value]) => {
                                  const SpecIcon = getSpecIcon(key);
                                  return (
                                    <div
                                      key={key}
                                      className="grid grid-cols-1 gap-2 px-0.5 py-1 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center xl:gap-4"
                                    >
                                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                        <span className="inline-flex aspect-square h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                                          <SpecIcon className="h-4 w-4" />
                                        </span>
                                        <span className="whitespace-normal pt-1 text-[10px] font-semibold uppercase leading-tight tracking-[0.16em] text-white/52">
                                          {fieldMetaByKey.get(key)?.label || formatSpecLabel(key)}
                                        </span>
                                      </div>
                                      <span className="break-words pl-10 text-left text-sm font-medium leading-relaxed text-white/88 xl:max-w-[13rem] xl:justify-self-end xl:pl-0 xl:pt-0.5 xl:text-right">
                                        {formatSpecValue(value, t)}
                                        {fieldMetaByKey.get(key)?.unit ? ` (${fieldMetaByKey.get(key).unit})` : ''}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                          <div className="mt-auto pt-2">
                            {showPrices ? (
                              activePromo?.kind === 'price' && Number(product?.price) > 0 ? (
                                <p className="mb-3 text-sm font-semibold text-[var(--dash-other)]">
                                  {formatTND(product.price)}
                                </p>
                              ) : null
                            ) : (
                              <p className="mb-3 text-sm font-semibold text-[var(--dash-other)]">
                                {t('common.priceOnRequest', 'Price on request')}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleAddToCart(product)}
                                className="inline-flex items-center gap-2 rounded-xl bg-[var(--dash-main)] px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-[#ff6b28]"
                              >
                                {t('productDetails.addToCart', 'Add to Cart')}
                              </button>
                              <Link
                                href={localizePath(`/machines/${product._id}`, language)}
                                className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/82 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white"
                              >
                                <ArrowUpRight className="h-3.5 w-3.5 text-[var(--dash-main)]" />
                                {t('productsPage.grid.viewDetails')}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
              );
            })}

            {totalPages > 1 ? (
              <div className="flex flex-col items-center justify-center gap-4 pt-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/75 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('common.previous', 'Previous')}
                </button>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  {visiblePages.map((page, index) => {
                    const prevPage = visiblePages[index - 1];
                    const showGap = index > 0 && page - prevPage > 1;
                    return (
                      <div key={`page-slot-${page}`} className="flex items-center gap-2">
                        {showGap ? <span className="text-sm text-white/35">...</span> : null}
                        <button
                          type="button"
                          onClick={() => onPageChange(page)}
                          aria-current={currentPage === page ? 'page' : undefined}
                          className={`inline-flex h-10 min-w-10 items-center justify-center rounded-xl border px-3 text-sm font-semibold transition-all duration-300 ${
                            currentPage === page
                              ? 'border-[var(--dash-main)] bg-[var(--dash-main)] text-white'
                              : 'border-white/15 text-white/70 hover:border-[var(--dash-main)] hover:text-white'
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/75 transition-all duration-300 hover:border-[var(--dash-main)] hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                >
                  {t('common.next', 'Next')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
